import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, RefreshCw, X, RotateCcw } from 'lucide-react';
import HUDFrame from './ui/HUDFrame';
import GlowButton from './ui/GlowButton';
import QRCodeDisplay from './ui/QRCodeDisplay';
import { UserData } from '../types';
import ArmyLogo from './united-states-army-2023-seeklogo.png';

interface RevealScreenProps {
  userData: UserData;
  sessionId: string | null;
  sessionCode: string | null;
  onRestart: () => void;
  onTryDifferentMOS: () => void;
  onStartNewSession: () => void;
}

const RevealScreen: React.FC<RevealScreenProps> = ({
  userData,
  sessionId,
  sessionCode,
  onRestart,
  onTryDifferentMOS,
  onStartNewSession,
}) => {
  const showQRCode = sessionId && sessionCode;

  // Dashboard modal state
  const [showDashboard, setShowDashboard] = useState(false);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Long-press handlers for QR code (3 seconds to open dashboard)
  const handleQRPressStart = (e: React.TouchEvent | React.MouseEvent) => {
    // Prevent default to stop browser context menu / text selection on long-press
    e.preventDefault();
    longPressTimerRef.current = setTimeout(() => {
      setShowDashboard(true);
    }, 3000);
  };

  const handleQRPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };


  return (
    <motion.div
      className="h-full w-full flex items-center justify-center bg-army-black tactical-grid"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <HUDFrame className="w-full max-w-7xl 4kl:max-w-[180rem] 4k:max-w-[95%] mx-auto p-4 md:p-5 lg:p-6 4kl:p-16 4k:p-16">
        <div className="text-center space-y-2 md:space-y-3 lg:space-y-3 4kl:space-y-8 4k:space-y-8">
          {/* Army Logo */}
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            <img src={ArmyLogo} alt="U.S. Army" className="h-8 md:h-10 lg:h-12 4kl:h-32 4k:h-20 w-auto mx-auto" />
          </motion.div>

          {/* Success Header */}
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <div className="flex justify-center mb-1 md:mb-2 lg:mb-2 4kl:mb-4 4k:mb-3">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: 'spring' }}
              >
                <CheckCircle className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 4kl:w-24 4kl:h-24 4k:w-16 4k:h-16 text-hud-green" />
              </motion.div>
            </div>
            <h2 className="text-sm md:text-base lg:text-lg 4kl:text-4xl 4k:text-3xl text-hud-green uppercase tracking-widest mb-1 4kl:mb-4 4k:mb-2">
              Transformation Complete
            </h2>
            <h1 className="text-xl md:text-2xl lg:text-3xl 4kl:text-7xl 4k:text-5xl font-bold text-white leading-none px-4 md:px-0">
              YOUR FUTURE AWAITS
            </h1>
          </motion.div>

          {/* MOS Info */}
          {userData.selectedMOS && (
            <motion.div
              className="text-army-gold text-xs md:text-sm lg:text-base 4kl:text-3xl 4k:text-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <p className="uppercase tracking-wider">
                {userData.selectedMOS.title}
              </p>
            </motion.div>
          )}

          {/* Main Content: Images + QR Code */}
          {/* 4K Portrait: column (QR below), 4K Landscape: row (QR beside but smaller) */}
          <div className="flex flex-col 4kl:flex-row gap-3 md:gap-4 lg:gap-5 4kl:gap-8 4k:gap-12 mt-2 md:mt-3 lg:mt-4 4kl:mt-6 4k:mt-8">
            {/* Two Generated Images Side by Side */}
            <div className="flex-1 grid grid-cols-2 gap-2 md:gap-3 lg:gap-4 4kl:gap-6 4k:gap-4">
              {/* Official Portrait */}
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <div className="relative">
                  <div className="relative aspect-[3/4] bg-black border-2 md:border-3 lg:border-4 4kl:border-8 4k:border-4 border-army-gold overflow-hidden">
                    {userData.generatedImages?.portrait ? (
                      <img
                        src={userData.generatedImages.portrait}
                        alt="Official Portrait"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500 text-xl">
                        Generation failed
                      </div>
                    )}

                    {/* Glow effect */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      animate={{
                        boxShadow: [
                          'inset 0 0 60px rgba(212, 175, 55, 0)',
                          'inset 0 0 60px rgba(212, 175, 55, 0.2)',
                          'inset 0 0 60px rgba(212, 175, 55, 0)',
                        ],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                      }}
                    />

                    {/* Corner accents */}
                    <div className="absolute top-0 left-0 w-6 h-6 4kl:w-12 4kl:h-12 4k:w-8 4k:h-8 border-t-2 border-l-2 4kl:border-t-4 4kl:border-l-4 border-army-gold" />
                    <div className="absolute top-0 right-0 w-6 h-6 4kl:w-12 4kl:h-12 4k:w-8 4k:h-8 border-t-2 border-r-2 4kl:border-t-4 4kl:border-r-4 border-army-gold" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 4kl:w-12 4kl:h-12 4k:w-8 4k:h-8 border-b-2 border-l-2 4kl:border-b-4 4kl:border-l-4 border-army-gold" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 4kl:w-12 4kl:h-12 4k:w-8 4k:h-8 border-b-2 border-r-2 4kl:border-b-4 4kl:border-r-4 border-army-gold" />

                    {/* Label overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 md:p-2 lg:p-3 4kl:p-6 4k:p-4">
                      <p className="text-xs md:text-xs lg:text-sm 4kl:text-2xl 4k:text-lg text-army-gold uppercase tracking-wider">
                        U.S. Army Private
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Field Action Shot */}
              <motion.div
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <div className="relative">
                  <div className="relative aspect-[3/4] bg-black border-2 md:border-3 lg:border-4 4kl:border-8 4k:border-4 border-hud-green overflow-hidden shadow-2xl shadow-army-gold/30">
                    {userData.generatedImages?.field ? (
                      <img
                        src={userData.generatedImages.field}
                        alt="Field Action"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500 text-xl">
                        Generation failed
                      </div>
                    )}

                    {/* Glow effect */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      animate={{
                        boxShadow: [
                          'inset 0 0 70px rgba(0, 255, 65, 0)',
                          'inset 0 0 70px rgba(0, 255, 65, 0.2)',
                          'inset 0 0 70px rgba(0, 255, 65, 0)',
                        ],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                      }}
                    />

                    {/* Corner accents with glow */}
                    <div className="absolute top-0 left-0 w-6 h-6 4kl:w-12 4kl:h-12 4k:w-8 4k:h-8 border-t-2 border-l-2 4kl:border-t-4 4kl:border-l-4 border-hud-green" />
                    <div className="absolute top-0 right-0 w-6 h-6 4kl:w-12 4kl:h-12 4k:w-8 4k:h-8 border-t-2 border-r-2 4kl:border-t-4 4kl:border-r-4 border-hud-green" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 4kl:w-12 4kl:h-12 4k:w-8 4k:h-8 border-b-2 border-l-2 4kl:border-b-4 4kl:border-l-4 border-hud-green" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 4kl:w-12 4kl:h-12 4k:w-8 4k:h-8 border-b-2 border-r-2 4kl:border-b-4 4kl:border-r-4 border-hud-green" />

                    {/* Label overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 md:p-2 lg:p-3 4kl:p-6 4k:p-4">
                      <p className="text-xs md:text-xs lg:text-sm 4kl:text-2xl 4k:text-lg text-hud-green uppercase tracking-wider">
                        {userData.selectedMOS?.shortTitle || 'In Action'}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* QR Code Section - Below on portrait, side on landscape */}
            {/* Long-press (3s) opens hidden dashboard */}
            {showQRCode && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="flex-shrink-0 flex items-center justify-center 4kl:w-48 4kl:self-center 4k:pt-4 select-none"
                style={{ touchAction: 'none' }}
                onMouseDown={handleQRPressStart}
                onMouseUp={handleQRPressEnd}
                onMouseLeave={handleQRPressEnd}
                onTouchStart={handleQRPressStart}
                onTouchEnd={handleQRPressEnd}
                onTouchCancel={handleQRPressEnd}
              >
                <div className="bg-army-dark/50 border border-army-gold/30 rounded-lg p-3 4kl:p-4 4k:p-6 select-none">
                  <QRCodeDisplay
                    sessionId={sessionId}
                    sessionCode={sessionCode}
                    mosId={userData.selectedMOS?.id || ''}
                    size={140}
                    className=""
                  />
                </div>
              </motion.div>
            )}
          </div>

          {/* Action Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-2 md:gap-2 lg:gap-3 4kl:gap-8 4k:gap-4 mt-2 md:mt-3 lg:mt-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6 }}
          >
            <GlowButton
              onClick={onTryDifferentMOS}
              variant="primary"
              size="large"
            >
              <div className="flex items-center space-x-2 4kl:space-x-3">
                <RefreshCw className="w-4 h-4 4kl:w-8 4kl:h-8 4k:w-6 4k:h-6" />
                <span className="text-sm 4kl:text-xl 4k:text-base">Try Different MOS</span>
              </div>
            </GlowButton>
            <GlowButton
              onClick={onStartNewSession}
              variant="secondary"
              size="large"
            >
              <div className="flex items-center space-x-2 4kl:space-x-3">
                <RotateCcw className="w-4 h-4 4kl:w-8 4kl:h-8 4k:w-6 4k:h-6" />
                <span className="text-sm 4kl:text-xl 4k:text-base">Start New Session</span>
              </div>
            </GlowButton>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            className="mt-2 md:mt-3 lg:mt-4 4kl:mt-6 4k:mt-4 p-2 md:p-2.5 lg:p-3 4kl:p-6 4k:p-4 bg-gradient-to-r from-army-dark to-transparent border-l-2 4kl:border-l-4 border-army-gold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8 }}
          >
            <p className="text-xs md:text-sm lg:text-base 4kl:text-3xl 4k:text-xl text-white mb-1 4kl:mb-3 4k:mb-2">
              Ready to make this vision a reality?
            </p>
            <p className="text-xs md:text-xs lg:text-sm 4kl:text-2xl 4k:text-lg text-army-gold">
              Visit GOARMY.COM to learn more about becoming a U.S. Army Soldier
            </p>
          </motion.div>

          {/* Bottom Status */}
          <motion.div
            className="pt-1 md:pt-2 lg:pt-2 4kl:pt-4 4k:pt-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ delay: 2 }}
          >
            <p className="text-xs 4kl:text-lg 4k:text-sm text-gray-500 uppercase tracking-widest px-4 md:px-0">
              Experience Complete • Scan QR Code to Download Photos
            </p>
          </motion.div>
        </div>
      </HUDFrame>

      {/* Dashboard Modal (shown after long-press on QR code) */}
      <AnimatePresence>
        {showDashboard && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/80"
              onClick={() => setShowDashboard(false)}
            />

            {/* Modal content */}
            <motion.div
              className="relative w-[95vw] h-[90vh] bg-army-dark border-2 border-army-gold rounded-lg overflow-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              {/* Close button */}
              <button
                onClick={() => setShowDashboard(false)}
                className="absolute top-4 right-4 z-10 p-2 bg-army-dark border border-army-gold/50 rounded-lg hover:bg-army-gold/20 transition-colors"
              >
                <X className="w-6 h-6 text-army-gold" />
              </button>

              {/* Dashboard iframe */}
              <iframe
                src="/dashboard"
                className="w-full h-full border-0"
                title="Dashboard"
                style={{ minHeight: '100%' }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default RevealScreen;
